// Recipes — per-user definitions of how to make a meal or snack.
//
// Recipes are PRIVATE (PK = the caller's Cognito Identity ID) under SK
// `recipe#<uuid>`. A recipe is a set of ingredient components and how many
// servings it yields:
//
//   { name, tags?, servingsMade, components: [{ ref, kind, servings }] }
//
// A component's `kind` is 'ingredient' (ref → an `ingredient#…` id) or 'recipe'
// (ref → a `recipe#…` id, i.e. a sub-recipe — see recipeNutrition/sourceEntry).
//
// `tags` is a free-form list of labels (e.g. "Breakfast", "Dinner", "Snack",
// "Healthy"). A recipe may have several — including more than one meal type
// (e.g. suitable as either lunch or dinner) — or none at all.
//
// STABLE RANDOM IDS: the <uuid> is a random v4 UUID minted once when the recipe
// is first created, and preserved across every subsequent edit. Editing a
// recipe's content (components, servingsMade) or its labels (name, tags) is
// therefore always an in-place update — the id never changes, so references to
// it from menus, stock (recipe-stock), archived menus, and parent recipes
// (sub-recipe components) stay valid.
//
// (Historical note: recipes previously used a CONTENT-ADDRESSED v5 UUID derived
// from components + servingsMade, which made imports idempotent and deduplicated
// identical recipes. That mattered for the old Lite n Easy "one recipe per meal"
// import, which has since been removed. Content addressing had a fatal downside
// for hand-authored recipes: any content edit minted a NEW id and orphaned every
// stored reference. Stable ids trade automatic dedup — no longer needed — for
// edit-safe references.)

import { getItem, listItems, updateItem, deleteItem, removeItemAttrs } from './db.js';
import { servingKj, servingMacro } from './ingredients.js';

/** SK prefix for recipe records in a user's private partition. */
export const RECIPE_PREFIX = 'recipe#';

/**
 * A recipe component is `{ ref, kind, servings }` where `kind` is `'ingredient'`
 * (ref is an `ingredient#…` id) or `'recipe'` (ref is a `recipe#…` id — a
 * sub-recipe). This is the ONLY stored shape (all rows migrated). The helpers
 * are the canonical accessors every consumer should use; they also tolerate the
 * pre-migration `{ ingredientRef | recipeRef }` shape purely defensively, so a
 * stray un-migrated object can never crash a read.
 */
export function componentRef(c) {
  return c?.ref ?? c?.ingredientRef ?? c?.recipeRef ?? null;
}

export function componentKind(c) {
  if (c?.kind) return c.kind;
  if (c?.recipeRef) return 'recipe';
  return 'ingredient';
}

/** True if a component references a sub-recipe (vs a raw ingredient). */
export function isRecipeComponent(c) {
  return componentKind(c) === 'recipe';
}

/** Normalise any component input to the canonical `{ ref, kind, servings }`. */
export function normaliseComponent(c) {
  return { ref: componentRef(c), kind: componentKind(c), servings: Number(c.servings) };
}

/** Mint a fresh random recipe id (`recipe#<uuidv4>`). */
function newRecipeId() {
  return RECIPE_PREFIX + crypto.randomUUID();
}

/** List all recipes for the signed-in user. */
export function listRecipes(doc, userId) {
  return listItems(doc, userId, RECIPE_PREFIX);
}

/** Fetch one recipe by its full itemId, or null if it doesn't exist. */
export function getRecipe(doc, userId, recipeId) {
  return getItem(doc, userId, recipeId);
}

/**
 * Create a recipe, or update an existing one in place. If `recipe.itemId` is
 * provided it's reused (an in-place edit); otherwise a fresh random id is
 * minted. Returns the stored recipe (including its itemId).
 *
 * @param recipe { itemId?, name, tags?, requiresPrep?, servingsMade,
 *                 components:[{ref,kind,servings}] }
 */
export async function ensureRecipe(doc, userId, recipe) {
  const itemId = recipe.itemId ?? newRecipeId();

  const stored = {
    name: recipe.name,
    servingsMade: Number(recipe.servingsMade),
    components: (recipe.components ?? []).map((c) => normaliseComponent(c)),
    // Whether MAKING this recipe is a preparation step. Default true.
    requiresPrep: recipe.requiresPrep ?? true,
  };
  if (recipe.tags && recipe.tags.length > 0) stored.tags = [...recipe.tags];
  // Preference `rating` (0..1) is a per-user label used to weight menu
  // generation. Stored ONLY when explicitly set, so unrated recipes track the
  // dynamic default at generation time (see model/ratings.js). An explicit 0
  // ("never") is kept.
  if (typeof recipe.rating === 'number' && Number.isFinite(recipe.rating)) {
    stored.rating = recipe.rating;
  }
  // Preserve a creation timestamp for newest-first ordering. Keep the existing
  // value when re-saving the same content; stamp a new one otherwise.
  stored.createdAt = recipe.createdAt ?? new Date().toISOString();

  // updateItem upserts by exact id (create on first save, in-place on edit).
  await updateItem(doc, userId, itemId, stored);
  // If the caller explicitly cleared the rating (passed null while editing a
  // previously-rated recipe), REMOVE any lingering value on the same-id row —
  // updateItem can only SET, so a cleared rating would otherwise persist.
  if (recipe.rating === null && !('rating' in stored)) {
    await removeItemAttrs(doc, userId, itemId, ['rating']);
  }
  return { userId, itemId, ...stored };
}

/**
 * Save a recipe. Editing an existing recipe (when `previous.itemId` is set) is
 * an in-place update that PRESERVES the id, so every stored reference to it
 * (menus, recipe-stock, archived menus, parent recipes) stays valid regardless
 * of what content or labels changed. Creating a new recipe mints a fresh id.
 *
 * @param previous the recipe as currently stored (with itemId), or null on create
 * @param next     the desired recipe content { name, tags?, servingsMade, components }
 * @returns the stored recipe (with its itemId)
 */
export async function saveRecipe(doc, userId, previous, next) {
  return ensureRecipe(doc, userId, {
    ...next,
    // Reuse the existing id on edit; mint a new one on create.
    itemId: previous?.itemId ?? next.itemId,
    // Carry the original creation time forward so ordering is stable across edits.
    createdAt: previous?.createdAt,
  });
}

/** Delete a recipe by its itemId. */
export function deleteRecipe(doc, userId, recipeId) {
  return deleteItem(doc, userId, recipeId);
}

/**
 * Compute a recipe's nutrition totals from its components.
 *
 * Each component contributes (its servings) × (the ingredient's per-serving
 * value). The whole-recipe total is the sum across components; the per-serving
 * figure divides that by servingsMade. Energy is in kJ; macros in grams.
 *
 * Any value that can't be computed (missing ingredient or missing nutrition on
 * an ingredient) is treated as 0 and flagged via `complete: false`, so the UI
 * can warn that the totals are partial.
 *
 * A `kind:'recipe'` component contributes (its servings) × (the sub-recipe's
 * PER-SERVING nutrition), computed recursively. Cycles (A→B→A) are guarded via a
 * visited set and treated as incomplete rather than looping forever.
 *
 * @param recipe          { servingsMade, components:[{ref,kind,servings}] }
 * @param ingredientsById Map<itemId, ingredient>
 * @param recipesById     Map<itemId, recipe> (optional; needed for sub-recipes)
 * @returns { perServing:{kj,protein,fat,carbs}, total:{kj,protein,fat,carbs}, complete }
 */
export function recipeNutrition(recipe, ingredientsById, recipesById = null, _visiting = null) {
  const total = { kj: 0, protein: 0, fat: 0, carbs: 0 };
  let complete = true;

  // Track the recursion stack to break cycles. Seeded lazily so top-level calls
  // needn't pass anything.
  const visiting = _visiting ?? new Set();
  if (recipe.itemId) visiting.add(recipe.itemId);

  for (const c of recipe.components ?? []) {
    const ref = componentRef(c);
    const servings = Number(c.servings) || 0;

    if (isRecipeComponent(c)) {
      const sub = recipesById?.get?.(ref);
      if (!sub || visiting.has(ref)) {
        // Missing sub-recipe or a cycle — can't complete.
        complete = false;
        continue;
      }
      const subNut = recipeNutrition(sub, ingredientsById, recipesById, visiting);
      if (!subNut.complete) complete = false;
      total.kj += subNut.perServing.kj * servings;
      total.protein += subNut.perServing.protein * servings;
      total.fat += subNut.perServing.fat * servings;
      total.carbs += subNut.perServing.carbs * servings;
      continue;
    }

    const ingredient = ingredientsById.get(ref);
    if (!ingredient) {
      complete = false;
      continue;
    }
    const kj = servingKj(ingredient);
    const protein = servingMacro(ingredient, 'protein');
    const fat = servingMacro(ingredient, 'fat');
    const carbs = servingMacro(ingredient, 'carbs');
    if (kj === null || protein === null || fat === null || carbs === null) complete = false;
    total.kj += (kj ?? 0) * servings;
    total.protein += (protein ?? 0) * servings;
    total.fat += (fat ?? 0) * servings;
    total.carbs += (carbs ?? 0) * servings;
  }

  if (recipe.itemId) visiting.delete(recipe.itemId);

  const made = Number(recipe.servingsMade) || 1;
  const perServing = {
    kj: total.kj / made,
    protein: total.protein / made,
    fat: total.fat / made,
    carbs: total.carbs / made,
  };
  return { perServing, total, complete };
}

/**
 * Would giving recipe `selfId` the given `components` create a dependency cycle?
 *
 * A cycle exists when a sub-recipe component (transitively) references `selfId`
 * itself. New recipes (no stable id yet, and not referenceable by anything) can
 * never cycle, so callers pass `selfId = null` for a create and skip the check.
 *
 * @param selfId       the id of the recipe being edited (null for a new recipe)
 * @param components   the proposed components [{ref,kind,servings}]
 * @param recipesById  Map<itemId, recipe> of all stored recipes
 * @returns true if any sub-recipe path leads back to `selfId`
 */
export function wouldCreateCycle(selfId, components, recipesById) {
  if (!selfId) return false;
  const seen = new Set();
  const stack = (components ?? []).filter((c) => isRecipeComponent(c)).map((c) => componentRef(c));
  while (stack.length > 0) {
    const ref = stack.pop();
    if (ref === selfId) return true;
    if (seen.has(ref)) continue;
    seen.add(ref);
    const sub = recipesById?.get?.(ref);
    for (const c of sub?.components ?? []) {
      if (isRecipeComponent(c)) stack.push(componentRef(c));
    }
  }
  return false;
}

/**
 * Prefix marking a synthesised virtual recipe id (an ingredient used directly as
 * a single-serve meal — not a stored recipe row).
 */
export const INGREDIENT_RECIPE_PREFIX = 'recipe#ingredient:';

/** True if a recipe id denotes a synthesised virtual recipe (vs a stored row). */
export function isIngredientRecipeId(id) {
  return typeof id === 'string' && id.startsWith(INGREDIENT_RECIPE_PREFIX);
}

/**
 * Synthesise a virtual 1-serving recipe from an ingredient, so an ingredient can
 * be placed directly as a single-serve meal by the generator (governed by a
 * strategy's `tagsMatchIngredients` flag — see model/menu.js). These are NOT
 * stored as recipe rows; the id encodes the source ingredient so it's stable and
 * resolvable, and the virtual recipe carries the ingredient's OWN tags so tag
 * matching works for any tag (not just "Snack"). Eating it is never prep.
 *
 * @param ingredient a shared ingredient record (must have itemId + name)
 * @returns a recipe-shaped object { itemId, name, tags, requiresPrep:false,
 *   servingsMade:1, components:[{ref, kind:'ingredient', servings:1}], virtual:true }
 */
export function virtualRecipeForIngredient(ingredient) {
  return {
    itemId: `${INGREDIENT_RECIPE_PREFIX}${ingredient.itemId}`,
    name: ingredient.name,
    tags: [...(ingredient.tags ?? [])],
    requiresPrep: false,
    servingsMade: 1,
    components: [{ ref: ingredient.itemId, kind: 'ingredient', servings: 1 }],
    virtual: true,
  };
}

/**
 * Build a resolver that flattens a recipe's whole component tree into searchable
 * strings: every ingredient name plus every sub-recipe name, recursing through
 * sub-recipes so searching e.g. "beef" matches a parent whose sub-recipe uses
 * beef. Cycle-guarded against stale data. Returns `(recipe) => string[]`,
 * suitable as the `componentTerms` arg to `filterRecipes`.
 */
export function makeComponentTermsResolver({ ingredientsById, recipesById }) {
  const nameOfIngredient = (ref) => ingredientsById?.get?.(ref)?.name ?? '';
  return function componentTermsFor(recipe, _visiting = null) {
    const visiting = _visiting ?? new Set();
    if (recipe?.itemId && visiting.has(recipe.itemId)) return [];
    if (recipe?.itemId) visiting.add(recipe.itemId);
    const terms = [];
    for (const c of recipe?.components ?? []) {
      const ref = componentRef(c);
      if (isRecipeComponent(c)) {
        const sub = recipesById?.get?.(ref);
        terms.push(sub?.name ?? String(ref).replace(/^recipe#/, ''));
        if (sub) terms.push(...componentTermsFor(sub, visiting));
      } else {
        terms.push(nameOfIngredient(ref));
      }
    }
    return terms;
  };
}

/**
 * Collect the distinct set of INGREDIENT refs (`ingredient#…`) a recipe uses,
 * recursing through sub-recipe components (cycle-guarded). Used to decide whether
 * a menu entry still depends on an ingredient that's on the shopping list.
 * @returns {Set<string>} the ingredient refs (sub-recipe refs are NOT included).
 */
export function recipeIngredientRefs(recipe, recipesById = null, _visiting = null, _out = null) {
  const out = _out ?? new Set();
  const visiting = _visiting ?? new Set();
  if (recipe?.itemId && visiting.has(recipe.itemId)) return out;
  if (recipe?.itemId) visiting.add(recipe.itemId);
  for (const c of recipe?.components ?? []) {
    const ref = componentRef(c);
    if (isRecipeComponent(c)) {
      const sub = recipesById?.get?.(ref);
      if (sub) recipeIngredientRefs(sub, recipesById, visiting, out);
    } else {
      out.add(ref);
    }
  }
  return out;
}

/** Case-insensitive test for whether a tag set contains a given tag. */
export function hasTag(tags, tag) {
  const want = String(tag).toLowerCase();
  return (tags ?? []).some((t) => String(t).toLowerCase() === want);
}

/**
 * Collect the distinct tags used across a set of recipes, sorted
 * case-insensitively. Handy for tag autocomplete suggestions.
 */
export function collectRecipeTags(recipes) {
  const seen = new Map(); // lowercased -> original casing (first seen)
  for (const r of recipes ?? []) {
    for (const t of r.tags ?? []) {
      const key = t.toLowerCase();
      if (!seen.has(key)) seen.set(key, t);
    }
  }
  return [...seen.values()].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}
